import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { PracticalExamSheetInput, PracticalExamSheetStatus, PracticalExamVehicle } from '@lms/shared-types';
import { Loader, useToast } from '@lms/ui';
import { HubBackLink } from '@/components/hub/HubBackLink';
import { api } from '@/lib/api';
import { roleActionErrorToast } from '@/lib/user-feedback';

export interface PracticalExamStudent {
  id: string;
  name: string;
  email: string;
  run: string;
}

export interface PracticalExamVehicleChoice {
  id: string;
  plate: string;
  brand: string;
  model: string;
  year: number | null;
  color: string;
  transmission: PracticalExamVehicle;
  active: boolean;
}

const FIELD =
  'w-full rounded-md border border-dash-outline-variant bg-white px-3 py-2 text-sm text-dash-on-surface outline-none focus:border-dash-secondary focus:ring-2 focus:ring-dash-secondary/20';

const COMPACT =
  'w-full rounded-md border border-dash-outline-variant bg-white px-2 py-1.5 text-sm text-dash-on-surface outline-none focus:border-dash-secondary focus:ring-2 focus:ring-dash-secondary/20';

const LABEL = 'text-[11px] font-medium tracking-wide text-dash-on-surface-variant uppercase';

function studentInitials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  const first = parts[0]?.[0] ?? '';
  const second = parts[1]?.[0] ?? parts[0]?.[1] ?? '';
  return (first + second).toUpperCase();
}

const PHASE_COPY = {
  student: {
    kicker: 'Paso 1 de 7',
    title: 'Elegir alumno',
    text: 'Busque al postulante por nombre, correo o R.U.N.',
    back: 'Volver a fichas',
    continue: 'Continuar',
  },
  vehicle: {
    kicker: 'Paso 2 de 7',
    title: 'Seleccionar vehículo',
    text: 'Elija el vehículo con el que rendirá el examen práctico el postulante.',
    back: 'Volver al alumno',
    continue: 'Continuar',
  },
  km: {
    kicker: 'Paso 3 de 7',
    title: 'Kilometraje inicial',
    text: 'Ingrese el kilometraje del vehículo al inicio del examen.',
    back: 'Volver al vehículo',
    continue: 'Continuar a la ficha',
  },
  details: {
    kicker: 'Datos de la ficha',
    title: 'Editar datos',
    text: 'Puede cambiar el alumno, el vehículo y el resto de los datos del examen.',
    back: 'Volver a la ficha',
    continue: 'Continuar a la ficha',
  },
} as const;

export function ExamStepPills({ step, status }: { step?: number; status: PracticalExamSheetStatus }) {
  const draft = status !== 'final';
  return (
    <div className="flex flex-wrap items-center gap-1.5">
      <span
        className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-semibold ${
          draft ? 'bg-[#dbe4ff] text-[#1e3a8a]' : 'bg-[#e8eaf0] text-[#334155]'
        }`}
      >
        <span className={`h-1.5 w-1.5 rounded-full ${draft ? 'bg-[#1e3a8a]' : 'bg-[#475569]'}`} aria-hidden="true" />
        {draft ? 'Borrador' : 'Finalizada'}
      </span>
      {step ? (
        <span className="inline-flex items-center rounded-full bg-[#eef2fb] px-2.5 py-1 text-xs font-semibold tracking-wide text-[#1e3a8a] uppercase">
        Paso {step} de 7
      </span>
      ) : null}
    </div>
  );
}

export function ExamStepHeader({
  title,
  text,
  step,
  status,
  backLabel,
  onBack,
  backTo = '/examen-practico',
  className = '',
}: {
  title: string;
  text?: string;
  step: number;
  status: PracticalExamSheetStatus;
  backLabel: string;
  onBack?: () => void;
  backTo?: string;
  className?: string;
}) {
  return (
    <div className={`mb-8 flex flex-col gap-4 ${className}`.trim()}>
      <div className="flex flex-col justify-between gap-4 sm:flex-row sm:items-center">
        <div className="flex items-start gap-4">
          <HubBackLink to={backTo} label={backLabel} onBack={onBack} />
          <div className="flex min-w-0 flex-1 flex-col">
            <h1 className="text-3xl font-semibold text-dash-primary md:text-4xl">{title}</h1>
            {text ? <p className="mt-1 max-w-2xl text-dash-on-surface-variant">{text}</p> : null}
          </div>
        </div>
        <ExamStepPills step={step} status={status} />
      </div>
      <div className="h-1.5 w-full overflow-hidden rounded-full bg-[#e2e8f0]">
        <div className="h-full rounded-full bg-dash-primary" style={{ width: `${(step / 7) * 100}%` }} />
      </div>
    </div>
  );
}

export function SheetStatusSection({ status }: { status: PracticalExamSheetStatus }) {
  const draft = status !== 'final';
  return (
    <section className="rounded-xl border border-dash-outline-variant bg-white p-4 shadow-sm md:p-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className="text-xs font-semibold tracking-[0.18em] text-dash-primary uppercase">Estado de la ficha</p>
          <p className="mt-1 text-sm text-dash-on-surface-variant">
            {draft
              ? 'Si pasas a otro apartado con la ficha activa o durante la práctica, queda en borrador para seguir después.'
              : 'La práctica quedó cerrada. Salir a otro apartado ya no la vuelve a abrir.'}
          </p>
        </div>
        {draft ? (
          <span className="inline-flex items-center gap-1.5 rounded-full bg-amber-50 px-2.5 py-1 text-xs font-medium text-amber-800">
            <span className="h-1.5 w-1.5 rounded-full bg-amber-500" aria-hidden="true" />
            Borrador
          </span>
        ) : (
          <span className="inline-flex items-center gap-1.5 rounded-full bg-slate-100 px-2.5 py-1 text-xs font-medium text-slate-700">
            <span className="h-1.5 w-1.5 rounded-full bg-slate-500" aria-hidden="true" />
            Finalizada
          </span>
        )}
      </div>
    </section>
  );
}

export function PracticalExamStudentStep({
  phase,
  sheet,
  studentUserId,
  initialVehicleId = '',
  disabled = false,
  onBack,
  onPatch,
  onStudent,
  onContinue,
}: {
  phase: 'student' | 'vehicle' | 'km' | 'details';
  sheet: PracticalExamSheetInput;
  studentUserId: string | null;
  initialVehicleId?: string;
  disabled?: boolean;
  onBack?: () => void;
  onPatch: (partial: Partial<PracticalExamSheetInput>) => void;
  onStudent: (student: PracticalExamStudent) => void;
  onContinue: () => void;
}) {
  const [search, setSearch] = useState('');
  const [term, setTerm] = useState('');
  const [vehicleId, setVehicleId] = useState(initialVehicleId);
  const [vehicleSearch, setVehicleSearch] = useState('');
  const [addingVehicle, setAddingVehicle] = useState(false);
  const [newVehicle, setNewVehicle] = useState({
    plate: '',
    brand: '',
    model: '',
    color: '',
    year: '',
    transmission: 'mechanical' as PracticalExamVehicle,
  });
  const [runFromProfile, setRunFromProfile] = useState(() => Boolean(studentUserId && sheet.run.trim()));
  const copy = PHASE_COPY[phase];
  const queryClient = useQueryClient();
  const { toast } = useToast();

  useEffect(() => {
    const timer = window.setTimeout(() => setTerm(search.trim()), 300);
    return () => window.clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    if (initialVehicleId) setVehicleId(initialVehicleId);
  }, [initialVehicleId]);

  const query = useQuery({
    queryKey: ['practical-exam-students', term],
    enabled: (phase === 'student' || phase === 'details') && term.length >= 1,
    queryFn: () => api<PracticalExamStudent[]>(`/practical-exams/students?search=${encodeURIComponent(term)}`),
  });

  const vehiclesQuery = useQuery({
    queryKey: ['practical-exam-vehicles', 'sheet-options'],
    enabled: phase === 'vehicle' || phase === 'details',
    queryFn: () => api<{ data: PracticalExamVehicleChoice[] }>('/practical-exams/vehicles?perPage=100'),
  });

  const vehicles = (vehiclesQuery.data?.data ?? []).filter((vehicle) => vehicle.active);
  const vehicleTerm = vehicleSearch.trim().toLowerCase();
  const visibleVehicles = vehicleTerm
    ? vehicles.filter((vehicle) =>
        `${vehicle.plate} ${vehicle.brand} ${vehicle.model} ${vehicle.color} ${vehicle.transmission === 'automatic' ? 'automático' : 'mecánico'}`.toLowerCase().includes(vehicleTerm),
      )
    : vehicles;
  const selectedVehicle = vehicles.find((vehicle) => vehicle.id === vehicleId) ?? null;

  const createVehicle = useMutation({
    mutationFn: () =>
      api<PracticalExamVehicleChoice>('/practical-exams/vehicles', {
        method: 'POST',
        body: {
          plate: newVehicle.plate,
          brand: newVehicle.brand,
          model: newVehicle.model,
          color: newVehicle.color,
          year: newVehicle.year.trim() === '' ? null : Number(newVehicle.year),
          transmission: newVehicle.transmission,
          notes: '',
          active: true,
        },
      }),
    onSuccess: async (saved) => {
      await queryClient.invalidateQueries({ queryKey: ['practical-exam-vehicles'] });
      setVehicleId(saved.id);
      onPatch({
        plate: saved.plate,
        vehicleType: saved.transmission === 'automatic' ? 'automatic' : 'mechanical',
      });
      setAddingVehicle(false);
      setNewVehicle({ plate: '', brand: '', model: '', color: '', year: '', transmission: 'mechanical' });
      toast({ title: 'Vehículo agregado', description: `${saved.plate} quedó disponible para el examen.`, variant: 'success' });
    },
    onError: (error) => toast(roleActionErrorToast(error, 'No se pudo agregar el vehículo')),
  });
  const nameLocked = Boolean(studentUserId);
  const studentReady = Boolean(studentUserId && sheet.applicantName.trim() && sheet.run.trim());
  const vehicleReady = Boolean(sheet.plate.trim() && sheet.vehicleType);
  const kmReady = sheet.kmStart != null && sheet.kmStart >= 0 && sheet.kmStart <= 9_999_999;
  const ready =
    phase === 'student'
      ? studentReady
      : phase === 'vehicle'
        ? vehicleReady
        : phase === 'km'
          ? kmReady
          : Boolean(
              sheet.applicantName.trim() &&
                sheet.run.trim() &&
                sheet.examDate &&
                sheet.examinerName.trim() &&
                sheet.vehicleType,
            );

  function choose(student: PracticalExamStudent) {
    setRunFromProfile(Boolean(student.run.trim()));
    onStudent(student);
  }

  function chooseVehicle(nextId: string) {
    setVehicleId(nextId);
    const vehicle = vehicles.find((item) => item.id === nextId);
    if (!vehicle) {
      onPatch({ plate: '', vehicleType: null });
      return;
    }
    onPatch({ plate: vehicle.plate, vehicleType: vehicle.transmission });
  }

  function submitNewVehicle() {
    const year = newVehicle.year.trim();
    if (year && (!Number.isInteger(Number(year)) || Number(year) < 1970 || Number(year) > 2100)) {
      toast({ title: 'El año no es válido', variant: 'danger' });
      return;
    }
    if (!newVehicle.plate.trim() || !newVehicle.brand.trim() || !newVehicle.model.trim()) return;
    createVehicle.mutate();
  }

  if (phase === 'student') {
    const results = query.data ?? [];
    const selectedOutsideResults =
      studentUserId && sheet.applicantName.trim() && !results.some((student) => student.id === studentUserId)
        ? { id: studentUserId, name: sheet.applicantName, email: '', run: sheet.run }
        : null;

    function renderStudentCard(student: PracticalExamStudent) {
      const selected = studentUserId === student.id;
      return (
        <button
          key={student.id}
          type="button"
          disabled={disabled}
          onClick={() => choose(student)}
          className={`flex w-full items-center justify-between gap-3 rounded-xl p-4 text-left transition-all ${
            selected
              ? 'border-2 border-[#384dd0] bg-[#eff4ff] shadow-sm'
              : 'border border-[#e2e8f0] bg-white hover:border-[#c7c5d2] hover:bg-[#eff4ff]'
          } ${disabled ? 'cursor-not-allowed opacity-60' : ''}`}
        >
          <span className="flex min-w-0 items-center gap-3.5">
            <span
              className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-full ${
                selected ? 'border-2 border-[#384dd0]' : 'border border-[#c7c5d2]'
              }`}
              aria-hidden="true"
            >
              <span className={`h-2.5 w-2.5 rounded-full ${selected ? 'bg-[#384dd0]' : 'bg-transparent'}`} />
            </span>
            <span
              className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-full text-sm font-bold shadow-sm ${
                selected ? 'bg-[#384dd0] text-white' : 'bg-[#dde9ff] text-[#080e5c]'
              }`}
              aria-hidden="true"
            >
              {studentInitials(student.name || 'Alumno')}
            </span>
            <span className="min-w-0">
              <span className={`block truncate text-base ${selected ? 'font-bold text-[#080e5c]' : 'font-semibold text-dash-on-surface'}`}>
                {student.name}
              </span>
              <span className="mt-0.5 block text-[13px] text-dash-on-surface-variant">
                R.U.N:{' '}
                <strong className="font-medium text-dash-on-surface">{student.run || 'Sin R.U.N.'}</strong>
              </span>
            </span>
          </span>
          <span className={`material-symbols-outlined shrink-0 ${selected ? 'text-[22px] text-[#384dd0]' : 'text-[20px] text-dash-on-surface-variant'}`} aria-hidden="true">
            {selected ? 'check_circle' : 'chevron_right'}
          </span>
        </button>
      );
    }

    return (
      <div className="lms-site-admin-hub-page font-sans">
        <ExamStepHeader
          title={copy.title}
          text={copy.text}
          step={1}
          status={sheet.status}
          backLabel={copy.back}
          onBack={onBack}
        />

        <div className="flex flex-col gap-6 rounded-xl bg-white p-6 shadow-sm sm:p-7">
          <div>
            <label className="mb-4 flex items-center gap-2 text-base font-semibold text-[#080e5c]" htmlFor="student-search-input">
              <span className="material-symbols-outlined text-[20px] text-[#384dd0]" aria-hidden="true">person_search</span>
              Buscar alumno
            </label>
            <div className="relative w-full">
              <span className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-4 text-dash-on-surface-variant" aria-hidden="true">
                <span className="material-symbols-outlined text-[22px]">search</span>
              </span>
              <input
                id="student-search-input"
                type="text"
                value={search}
                autoFocus
                disabled={disabled}
                placeholder="Buscar por nombre, correo o R.U.N...."
                onChange={(event) => setSearch(event.target.value)}
                className="h-12 w-full rounded-lg border border-[#e2e8f0] bg-white pr-12 pl-12 text-sm text-dash-on-surface shadow-sm outline-none placeholder:text-dash-on-surface-variant/60 focus:border-[#384dd0]"
              />
              {search ? (
                <button
                  type="button"
                  title="Limpiar búsqueda"
                  onClick={() => setSearch('')}
                  className="absolute inset-y-0 right-0 flex items-center pr-3.5 text-dash-on-surface-variant hover:text-[#080e5c]"
                >
                  <span className="material-symbols-outlined text-[20px]" aria-hidden="true">cancel</span>
                </button>
              ) : null}
            </div>
            <p className="mt-2 px-1 text-[13px] text-dash-on-surface-variant">
              Escriba al menos 1 caracter. Formato R.U.N: 18.234.567-8
            </p>
          </div>

          <div className="h-px bg-[#e6eeff]" />

          {selectedOutsideResults ? (
            <div className="flex flex-col gap-3">{renderStudentCard(selectedOutsideResults)}</div>
          ) : null}

          {term.length < 1 ? null : query.isLoading ? (
            <div className="flex justify-center py-6">
              <Loader />
            </div>
          ) : query.error ? (
            <p className="text-sm text-error-600" role="alert">
              {(query.error as Error).message}
            </p>
          ) : results.length === 0 ? (
            <p className="text-sm text-dash-on-surface-variant">No hay alumnos con ese dato.</p>
          ) : (
            <div className="flex flex-col gap-3">
              <p className="text-[12px] font-semibold tracking-wider text-dash-on-surface-variant uppercase">Alumnos disponibles</p>
              {results.map((student) => renderStudentCard(student))}
            </div>
          )}

          {studentUserId && !runFromProfile ? (
            <label className="block max-w-xs">
              <span className={LABEL}>R.U.N.</span>
              <input
                value={sheet.run}
                disabled={disabled}
                maxLength={20}
                onChange={(event) => onPatch({ run: event.target.value })}
                className={`${FIELD} mt-1`}
              />
              <span className="mt-1 block text-xs text-dash-on-surface-variant">Este alumno no tiene R.U.N. en su perfil.</span>
            </label>
          ) : null}

          <div className="flex flex-col-reverse items-center justify-end gap-3 pt-2 sm:flex-row">
            <button
              type="button"
              disabled={disabled || !ready}
              onClick={onContinue}
              className="group inline-flex h-12 w-full items-center justify-center gap-3 rounded-lg bg-[#080e5c] px-8 text-base font-semibold text-white shadow-md transition-all hover:bg-[#080e5c]/90 disabled:opacity-60 sm:w-auto"
            >
              Continuar al Paso 2: Vehículo
              <span className="material-symbols-outlined text-[20px] transition-transform group-hover:translate-x-1" aria-hidden="true">
                arrow_forward
              </span>
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (phase === 'vehicle') {
    return (
      <div className="lms-site-admin-hub-page font-sans">
        <ExamStepHeader
          title={copy.title}
          text={copy.text}
          step={2}
          status={sheet.status}
          backLabel={copy.back}
          onBack={onBack}
        />

        <div className="flex flex-col gap-6 rounded-xl bg-white p-6 shadow-sm sm:p-7">
          <div>
            <div className="mb-4 flex items-center justify-between gap-3">
              <label className="flex items-center gap-2 text-base font-semibold text-[#080e5c]" htmlFor="vehicle-search-input">
                <span className="material-symbols-outlined text-[20px] text-[#384dd0]" aria-hidden="true">directions_car</span>
                Buscar vehículo
              </label>
              <button
                type="button"
                disabled={disabled}
                onClick={() => setAddingVehicle((open) => !open)}
                className="inline-flex items-center justify-center gap-1.5 rounded-lg bg-[#eff4ff] px-3.5 py-2 text-[13px] font-semibold whitespace-nowrap text-[#384dd0] transition-colors hover:bg-[#e6eeff] disabled:opacity-60"
              >
                <span className="material-symbols-outlined text-[18px]" aria-hidden="true">add</span>
                Agregar no registrado
              </button>
            </div>
            <div className="relative w-full">
              <span className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-4 text-dash-on-surface-variant" aria-hidden="true">
                <span className="material-symbols-outlined text-[22px]">search</span>
              </span>
              <input
                id="vehicle-search-input"
                type="text"
                value={vehicleSearch}
                onChange={(event) => setVehicleSearch(event.target.value)}
                placeholder="Buscar por patente, marca o modelo..."
                className="h-12 w-full rounded-lg border border-[#e2e8f0] bg-white pr-12 pl-12 text-sm text-dash-on-surface shadow-sm outline-none placeholder:text-dash-on-surface-variant/60 focus:border-[#384dd0]"
              />
              {vehicleSearch ? (
                <button
                  type="button"
                  title="Limpiar búsqueda"
                  onClick={() => setVehicleSearch('')}
                  className="absolute inset-y-0 right-0 flex items-center pr-3.5 text-dash-on-surface-variant hover:text-[#080e5c]"
                >
                  <span className="material-symbols-outlined text-[20px]" aria-hidden="true">cancel</span>
                </button>
              ) : null}
            </div>
            <p className="mt-2 px-1 text-[13px] text-dash-on-surface-variant">
              Patente, marca, modelo o tipo de transmisión.
            </p>
          </div>

          <div className="h-px bg-[#e6eeff]" />

          {addingVehicle ? (
            <form
              className="grid gap-3 rounded-xl bg-[#eff4ff] p-4 sm:grid-cols-2"
              onSubmit={(event) => {
                event.preventDefault();
                submitNewVehicle();
              }}
            >
              <label className="block">
                <span className={LABEL}>Patente</span>
                <input
                  required
                  value={newVehicle.plate}
                  disabled={disabled || createVehicle.isPending}
                  onChange={(event) => setNewVehicle((current) => ({ ...current, plate: event.target.value.toUpperCase() }))}
                  className={`${FIELD} mt-1 uppercase`}
                />
              </label>
              <label className="block">
                <span className={LABEL}>Marca</span>
                <input
                  required
                  value={newVehicle.brand}
                  disabled={disabled || createVehicle.isPending}
                  onChange={(event) => setNewVehicle((current) => ({ ...current, brand: event.target.value }))}
                  className={`${FIELD} mt-1`}
                />
              </label>
              <label className="block">
                <span className={LABEL}>Modelo</span>
                <input
                  required
                  value={newVehicle.model}
                  disabled={disabled || createVehicle.isPending}
                  onChange={(event) => setNewVehicle((current) => ({ ...current, model: event.target.value }))}
                  className={`${FIELD} mt-1`}
                />
              </label>
              <label className="block">
                <span className={LABEL}>Color</span>
                <input
                  value={newVehicle.color}
                  disabled={disabled || createVehicle.isPending}
                  onChange={(event) => setNewVehicle((current) => ({ ...current, color: event.target.value }))}
                  className={`${FIELD} mt-1`}
                />
              </label>
              <label className="block">
                <span className={LABEL}>Año</span>
                <input
                  inputMode="numeric"
                  value={newVehicle.year}
                  disabled={disabled || createVehicle.isPending}
                  onChange={(event) => setNewVehicle((current) => ({ ...current, year: event.target.value.replace(/\D/g, '').slice(0, 4) }))}
                  className={`${FIELD} mt-1`}
                />
              </label>
              <fieldset className="block">
                <legend className={LABEL}>Transmisión</legend>
                <div className="mt-1 grid grid-cols-2 gap-2">
                  {(
                    [
                      ['mechanical', 'Mecánico'],
                      ['automatic', 'Automático'],
                    ] as const
                  ).map(([value, label]) => (
                    <label
                      key={value}
                      className={`flex cursor-pointer items-center justify-center rounded-lg border px-3 py-2 text-sm ${
                        newVehicle.transmission === value
                          ? 'border-dash-primary bg-white font-semibold text-dash-primary'
                          : 'border-[#d1d5db] bg-white text-slate-700'
                      }`}
                    >
                      <input
                        type="radio"
                        name="new-vehicle-transmission"
                        className="sr-only"
                        checked={newVehicle.transmission === value}
                        onChange={() => setNewVehicle((current) => ({ ...current, transmission: value }))}
                      />
                      {label}
                    </label>
                  ))}
                </div>
              </fieldset>
              <div className="flex justify-end gap-2 sm:col-span-2">
                <button
                  type="button"
                  onClick={() => setAddingVehicle(false)}
                  className="rounded-lg px-4 py-2 text-sm font-medium text-dash-on-surface"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={disabled || createVehicle.isPending}
                  className="rounded-lg bg-[#080e5c] px-4 py-2 text-sm font-semibold text-white disabled:opacity-60"
                >
                  {createVehicle.isPending ? 'Guardando…' : 'Agregar'}
                </button>
              </div>
            </form>
          ) : null}

          {vehiclesQuery.isLoading ? (
            <div className="flex justify-center py-8">
              <Loader />
            </div>
          ) : vehiclesQuery.error ? (
            <p className="text-sm text-error-600" role="alert">
              {(vehiclesQuery.error as Error).message}
            </p>
          ) : vehicles.length === 0 ? (
            <p className="text-sm text-dash-on-surface-variant">
              No hay vehículos disponibles.{' '}
              <Link to="/examen-practico/vehiculos" className="font-medium text-dash-primary">
                Agregar un vehículo
              </Link>
            </p>
          ) : visibleVehicles.length === 0 ? (
            <p className="text-sm text-dash-on-surface-variant">No hay vehículos con ese dato.</p>
          ) : (
            <div className="flex flex-col gap-3" role="radiogroup" aria-label="Vehículos disponibles">
              <p className="text-[12px] font-semibold tracking-wider text-dash-on-surface-variant uppercase">Vehículos disponibles</p>
              {visibleVehicles.map((vehicle) => {
                const selected = selectedVehicle?.id === vehicle.id;
                const transmission = vehicle.transmission === 'automatic' ? 'Automático' : 'Mecánico';
                const details = [vehicle.color, vehicle.year ? String(vehicle.year) : '', transmission].filter(Boolean);
                return (
                  <label
                    key={vehicle.id}
                    className={`flex w-full cursor-pointer items-center justify-between gap-3 rounded-xl p-4 text-left transition-all ${
                      selected
                        ? 'border-2 border-[#384dd0] bg-[#eff4ff] shadow-sm'
                        : 'border border-[#e2e8f0] bg-white hover:border-[#c7c5d2] hover:bg-[#eff4ff]'
                    } ${disabled ? 'cursor-not-allowed opacity-60' : ''}`}
                  >
                    <input
                      type="radio"
                      name="selected_vehicle"
                      className="sr-only"
                      value={vehicle.plate}
                      checked={selected}
                      disabled={disabled}
                      onChange={() => chooseVehicle(vehicle.id)}
                    />
                    <span className="flex min-w-0 items-center gap-3.5">
                      <span
                        className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-full ${
                          selected ? 'border-2 border-[#384dd0]' : 'border border-[#c7c5d2]'
                        }`}
                        aria-hidden="true"
                      >
                        <span className={`h-2.5 w-2.5 rounded-full ${selected ? 'bg-[#384dd0]' : 'bg-transparent'}`} />
                      </span>
                      <span
                        className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-full shadow-sm ${
                          selected ? 'bg-[#384dd0] text-white' : 'bg-[#dde9ff] text-[#080e5c]'
                        }`}
                        aria-hidden="true"
                      >
                        <span className="material-symbols-outlined text-[22px]">directions_car</span>
                      </span>
                      <span className="min-w-0">
                        <span className={`block truncate text-base ${selected ? 'font-bold text-[#080e5c]' : 'font-semibold text-dash-on-surface'}`}>
                          {vehicle.brand} {vehicle.model}
                        </span>
                        <span className="mt-0.5 block text-[13px] text-dash-on-surface-variant">
                          Patente: <strong className="font-medium text-dash-on-surface">{vehicle.plate}</strong>
                          {` • ${details.join(' • ')}`}
                        </span>
                      </span>
                    </span>
                    <span className="flex shrink-0 items-center gap-3">
                      <span className="hidden items-center gap-1.5 rounded-full bg-[#f1f5f9] px-2.5 py-1 text-[12px] font-semibold tracking-wide text-dash-on-surface uppercase shadow-sm sm:inline-flex">
                        <span className="h-2 w-2 rounded-full bg-[#384dd0]" aria-hidden="true" />
                        Disponible
                      </span>
                      <span
                        className={`material-symbols-outlined ${selected ? 'text-[22px] text-[#384dd0]' : 'text-[20px] text-dash-on-surface-variant'}`}
                        aria-hidden="true"
                      >
                        {selected ? 'check_circle' : 'chevron_right'}
                      </span>
                    </span>
                  </label>
                );
              })}
            </div>
          )}

          <div className="flex flex-col-reverse items-center justify-end gap-3 pt-2 sm:flex-row">
            <button
              type="button"
              disabled={disabled || !ready}
              onClick={onContinue}
              className="group inline-flex h-12 w-full items-center justify-center gap-3 rounded-lg bg-[#080e5c] px-8 text-base font-semibold text-white shadow-md transition-all hover:bg-[#080e5c]/90 disabled:opacity-60 sm:w-auto"
            >
              Continuar al Paso 3: Kilometraje
              <span className="material-symbols-outlined text-[20px] transition-transform group-hover:translate-x-1" aria-hidden="true">
                arrow_forward
              </span>
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="lms-site-admin-hub-page font-sans">
      {phase === 'km' ? (
        <ExamStepHeader
          title={copy.title}
          text={copy.text}
          step={3}
          status={sheet.status}
          backLabel={copy.back}
          onBack={onBack}
        />
      ) : (
        <div className="mb-6 flex items-start gap-4">
          <HubBackLink to="/examen-practico" label={copy.back} onBack={onBack} />
          <div>
            <p className="text-xs font-semibold tracking-[0.18em] text-dash-primary uppercase">{copy.kicker}</p>
            <h1 className="mt-2 text-3xl font-semibold text-dash-primary md:text-4xl">{copy.title}</h1>
            <p className="mt-1 text-dash-on-surface-variant">{copy.text}</p>
          </div>
        </div>
      )}

      {phase === 'details' ? (
        <div className="mb-4">
          <SheetStatusSection status={sheet.status} />
        </div>
      ) : null}

      {phase === 'km' ? (
        <section className="rounded-xl border border-dash-outline-variant bg-white p-4 shadow-sm md:p-6">
          <div className="space-y-4">
            <label className="block text-base font-semibold text-dash-on-surface" htmlFor="initialKmInput">
              Km inicial <span className="font-bold text-error-600">*</span>
            </label>
            <div className="relative rounded-lg shadow-sm">
              <span className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-4 text-dash-on-surface-variant" aria-hidden="true">
                <span className="material-symbols-outlined text-[22px]">pin</span>
              </span>
              <input
                id="initialKmInput"
                name="km_inicial"
                type="text"
                inputMode="numeric"
                autoFocus
                value={sheet.kmStart ?? ''}
                disabled={disabled}
                placeholder="Ej: 45200"
                onChange={(event) => {
                  const digits = event.target.value.replace(/\D/g, '').slice(0, 7);
                  onPatch({ kmStart: digits === '' ? null : Number(digits) });
                }}
                className="w-full rounded-lg bg-[#eff4ff] py-3.5 pr-16 pl-12 text-2xl font-semibold text-dash-on-surface outline-none transition-all focus:bg-white focus:ring-2 focus:ring-[#384dd0] disabled:opacity-60"
              />
              <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-4 text-[12px] font-semibold tracking-wider text-dash-on-surface-variant uppercase" aria-hidden="true">
                Km
              </span>
            </div>
            <div className="flex items-center gap-2 pt-1 text-[13px] text-dash-on-surface-variant">
              <span className="material-symbols-outlined text-[18px] text-[#384dd0]" aria-hidden="true">info</span>
              <span>Escriba el número entero, sin puntos ni comas.</span>
            </div>
          </div>
          <div className="mt-5 flex justify-end border-t border-dash-outline-variant pt-4">
            <button
              type="button"
              disabled={disabled || !ready}
              onClick={onContinue}
              className="rounded-lg bg-dash-primary px-5 py-2.5 text-sm font-medium text-white disabled:opacity-60"
            >
              {copy.continue}
            </button>
          </div>
        </section>
      ) : null}

      {phase === 'details' ? (
        <>
          <section className="rounded-xl border border-dash-outline-variant bg-white p-4 shadow-sm md:p-6">
            <label className="block">
              <span className={LABEL}>Buscar alumno</span>
              <input
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder="Nombre, correo o R.U.N."
                className={`${FIELD} mt-1`}
                disabled={disabled}
              />
            </label>

            <label className="mt-5 block">
              <span className={LABEL}>Seleccionar vehículo</span>
              <select
                value={selectedVehicle?.id ?? ''}
                disabled={disabled || vehiclesQuery.isLoading || vehicles.length === 0}
                onChange={(event) => chooseVehicle(event.target.value)}
                className={`${FIELD} mt-1`}
              >
                <option value="">{vehiclesQuery.isLoading ? 'Cargando vehículos…' : 'Seleccione un vehículo'}</option>
                {vehicles.map((vehicle) => {
                  const type = vehicle.transmission === 'automatic' ? 'Automático' : 'Mecánico';
                  const year = vehicle.year ? ` ${vehicle.year}` : '';
                  return (
                    <option key={vehicle.id} value={vehicle.id}>
                      {`${vehicle.plate} · ${vehicle.brand} ${vehicle.model}${year} · ${type}`}
                    </option>
                  );
                })}
              </select>
              {vehiclesQuery.error ? (
                <span className="mt-1 block text-xs text-error-600" role="alert">
                  {(vehiclesQuery.error as Error).message}
                </span>
              ) : vehicles.length === 0 && !vehiclesQuery.isLoading ? (
                <span className="mt-1 block text-xs text-dash-on-surface-variant">
                  No hay vehículos disponibles.{' '}
                  <Link to="/examen-practico/vehiculos" className="font-medium text-dash-primary">
                    Agregar un vehículo
                  </Link>
                </span>
              ) : (
                <span className="mt-1 block text-xs text-dash-on-surface-variant">
                  Al elegirlo se completan la patente y si es mecánico o automático.
                </span>
              )}
            </label>

            {term.length < 1 ? (
              <p className="mt-4 text-sm text-dash-on-surface-variant">Escriba al menos 1 caracter.</p>
            ) : query.isLoading ? (
              <div className="mt-6 flex justify-center">
                <Loader />
              </div>
            ) : query.error ? (
              <p className="mt-4 text-sm text-error-600" role="alert">
                {(query.error as Error).message}
              </p>
            ) : (query.data ?? []).length === 0 ? (
              <p className="mt-4 text-sm text-dash-on-surface-variant">No hay alumnos con ese dato.</p>
            ) : (
              <ul className="mt-4 divide-y divide-dash-outline-variant overflow-hidden rounded-lg border border-dash-outline-variant">
                {(query.data ?? []).map((student) => {
                  const active = studentUserId === student.id;
                  return (
                    <li key={student.id}>
                      <button
                        type="button"
                        onClick={() => choose(student)}
                        disabled={disabled}
                        className={`flex w-full items-center justify-between gap-3 px-3 py-3 text-left ${active ? 'bg-secondary-50' : 'bg-white hover:bg-secondary-50/60'}`}
                      >
                        <span>
                          <span className="block text-sm font-medium text-dash-on-surface">{student.name}</span>
                          <span className="block text-xs text-dash-on-surface-variant">{student.email}</span>
                        </span>
                        <span className="text-sm text-dash-on-surface">{student.run || 'Sin R.U.N.'}</span>
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </section>

          <section className="mt-4 rounded-xl border border-dash-outline-variant bg-white p-4 shadow-sm md:p-6">
            <div className="mb-4 border-b border-dash-outline-variant pb-3">
              <p className="text-xs font-semibold tracking-[0.18em] text-dash-primary uppercase">Ficha de evaluación</p>
              <p className="text-lg font-semibold text-dash-on-surface">Examen práctico de conducción · clase B</p>
            </div>
            <div className="grid gap-3 md:grid-cols-12">
              <label className="md:col-span-6">
                <span className={LABEL}>Nombres y apellidos del postulante</span>
                <input
                  value={sheet.applicantName}
                  readOnly={nameLocked}
                  disabled={disabled || (!nameLocked && !sheet.applicantName)}
                  onChange={(event) => onPatch({ applicantName: event.target.value })}
                  className={`${COMPACT} mt-1 read-only:bg-secondary-50`}
                />
              </label>
              <label className="md:col-span-3">
                <span className={LABEL}>R.U.N.</span>
                <input
                  value={sheet.run}
                  readOnly={runFromProfile}
                  disabled={disabled || (!studentUserId && !sheet.run)}
                  onChange={(event) => onPatch({ run: event.target.value })}
                  className={`${COMPACT} mt-1 read-only:bg-secondary-50`}
                />
                {studentUserId && !runFromProfile ? (
                  <span className="mt-1 block text-xs text-dash-on-surface-variant">Este alumno no tiene R.U.N. en su perfil.</span>
                ) : null}
              </label>
              <label className="md:col-span-3">
                <span className={LABEL}>Fecha del examen</span>
                <input
                  type="date"
                  value={sheet.examDate}
                  disabled={disabled}
                  onChange={(event) => onPatch({ examDate: event.target.value })}
                  className={`${COMPACT} mt-1`}
                />
              </label>
              <label className="md:col-span-4">
                <span className={LABEL}>Nombre del examinador</span>
                <input
                  value={sheet.examinerName}
                  disabled={disabled}
                  onChange={(event) => onPatch({ examinerName: event.target.value })}
                  className={`${COMPACT} mt-1`}
                />
              </label>
              <label className="md:col-span-4">
                <span className={LABEL}>Municipio</span>
                <input value={sheet.municipality} readOnly className={`${COMPACT} mt-1 read-only:bg-secondary-50`} />
                <span className="mt-1 block text-xs text-dash-on-surface-variant">Se toma de Assets del sitio.</span>
              </label>
              <label className="md:col-span-2">
                <span className={LABEL}>Hora de inicio</span>
                <input type="time" value={sheet.startedAt} readOnly className={`${COMPACT} mt-1 read-only:bg-secondary-50`} />
                <span className="mt-1 block text-xs text-dash-on-surface-variant">Se registra al continuar desde el km inicial.</span>
              </label>
              <label className="md:col-span-2">
                <span className={LABEL}>Hora de término</span>
                <input type="time" value={sheet.endedAt} readOnly className={`${COMPACT} mt-1 read-only:bg-secondary-50`} />
                <span className="mt-1 block text-xs text-dash-on-surface-variant">Se registra al pulsar Terminar en la ficha.</span>
              </label>
              <label className="md:col-span-2">
                <span className={LABEL}>Km inicial</span>
                <input
                  inputMode="numeric"
                  value={sheet.kmStart ?? ''}
                  disabled={disabled}
                  onChange={(event) => {
                    const digits = event.target.value.replace(/\D/g, '').slice(0, 7);
                    onPatch({ kmStart: digits === '' ? null : Number(digits) });
                  }}
                  className={`${COMPACT} mt-1`}
                />
              </label>
              <label className="md:col-span-2">
                <span className={LABEL}>Km final</span>
                <input
                  inputMode="numeric"
                  value={sheet.kmEnd ?? ''}
                  disabled={disabled}
                  onChange={(event) => {
                    const digits = event.target.value.replace(/\D/g, '').slice(0, 7);
                    onPatch({ kmEnd: digits === '' ? null : Number(digits) });
                  }}
                  className={`${COMPACT} mt-1`}
                />
              </label>
              <label className="md:col-span-4">
                <span className={LABEL}>Escuela de conductores</span>
                <input value={sheet.school} readOnly className={`${COMPACT} mt-1 read-only:bg-secondary-50`} />
              </label>
              <label className="md:col-span-4">
                <span className={LABEL}>Comuna de la escuela</span>
                <input value={sheet.schoolCommune} readOnly className={`${COMPACT} mt-1 read-only:bg-secondary-50`} />
              </label>
              <label className="md:col-span-2">
                <span className={LABEL}>Patente</span>
                <input value={sheet.plate} readOnly disabled={disabled} className={`${COMPACT} mt-1 uppercase read-only:bg-secondary-50`} />
              </label>
              <fieldset className="md:col-span-2">
                <legend className={LABEL}>Vehículo</legend>
                <div className="mt-2 flex gap-3 text-sm">
                  <label className="inline-flex items-center gap-1.5">
                    <input type="checkbox" checked={sheet.vehicleType === 'mechanical'} disabled className="pointer-events-none" />
                    Mecánico
                  </label>
                  <label className="inline-flex items-center gap-1.5">
                    <input type="checkbox" checked={sheet.vehicleType === 'automatic'} disabled className="pointer-events-none" />
                    Automático
                  </label>
                </div>
              </fieldset>
            </div>
          </section>

          <div className="mt-4 flex justify-end">
            <button
              type="button"
              disabled={disabled || !ready}
              onClick={onContinue}
              className="rounded-lg bg-dash-primary px-5 py-2.5 text-sm font-medium text-white disabled:opacity-60"
            >
              {copy.continue}
            </button>
          </div>
        </>
      ) : null}
    </div>
  );
}
